import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack, Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';

import SlideInModal from 'components/Modal/SlideInModal';
import { SideContent } from 'components-shared';
import Loading from 'components/global/Loading';

import useConnectedUsers from '../use-connected-users';
import { formatBytes, formatDateTime } from '../helpers';
import InfoLine from './InfoLine';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CardContent: React.FC<{ user: any; onClose: () => void }> = ({ user, onClose }) => {
  const { t } = useTranslation();
  
  const fullName = useMemo(() => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
    if (name) return name;
    
    const emailUsername = user.email?.split('@')[0] || user.email;
    return emailUsername;
  }, [user.first_name, user.last_name, user.email]);

  return (
    <Stack
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid #EFEFEF',
        borderRadius: '24px',
        backgroundColor: '#FFFFFF',
        p: 3
      }}
    >
      <Stack 
        direction="row" 
        alignItems="center" 
        justifyContent="space-between"
        sx={{ pt: 1, pb: 3 }}
      >
        <Box
          sx={{
            border: '1px solid #EFEFEF',
            borderRadius: '50px',
            px: 2,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <i className="fa-light fa-mobile" style={{ fontSize: 12, color: '#2570EB' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#464646' }}>
            {t('supervision.connected_users.card.devices_count', { count: user.devices_count })}
          </Typography>
        </Box>

        <IconButton 
          onClick={onClose} 
          sx={{ 
            p: 0,
            width: '24px',
            height: '24px',
            minWidth: '24px',
            minHeight: '24px',
            borderRadius: '50%',
            '&:hover .close-icon': {
              backgroundColor: '#F7F7F7'
            }
          }}
        >
          <Box
            className="close-icon"
            sx={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'background-color 0.2s'
            }}
          >
            <i className="fa-light fa-xmark" style={{ fontSize: 12, color: '#464646' }} />
          </Box>
        </IconButton>
      </Stack>

      <Stack sx={{ pb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#464646' }}>
          {fullName}
        </Typography>
      </Stack>

      <Stack sx={{ pb: 2, gap: 0.5 }}>
        <InfoLine
          title={t('supervision.connected_users.card.email')}
          value={user.email}
        />
        <InfoLine
          title={t('supervision.connected_users.card.connected_since')}
          value={formatDateTime(user.connected_since)}
        />
        {user.country && (
          <InfoLine
            title={t('supervision.connected_users.card.country')}
            value={user.country}
          />
        )}
        {user.language && (
          <InfoLine
            title={t('supervision.connected_users.card.language')}
            value={user.language}
          />
        )}
        {user.device && (
          <InfoLine
            title={t('supervision.connected_users.card.device_type')}
            value={user.device}
          />
        )}
        {user.os_name && (
          <InfoLine
            title={t('supervision.connected_users.card.os')}
            value={user.os_name}
          />
        )}
        {user.browser_name && (
          <InfoLine
            title={t('supervision.connected_users.card.browser')}
            value={user.browser_name}
          />
        )}
      </Stack>

      <Box 
        sx={{ 
          pb: 3,
          overflowY: 'auto',
          flex: 1,
          width: '100%',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        <Stack gap={2}>
          {user.devices.map((device, index) => (
            <Box
              key={index}
              sx={{
                border: '1px solid #EFEFEF',
                borderRadius: '16px',
                p: 2
              }}
            >
              <Stack>
                <InfoLine
                  compact
                  title={t('supervision.connected_users.card.device.mac_address')}
                  value={device.mac_address}
                />
                <InfoLine
                  compact
                  title={t('supervision.connected_users.card.device.upload')}
                  value={formatBytes(device.upload)}
                />
                <InfoLine
                  compact
                  title={t('supervision.connected_users.card.device.download')}
                  value={formatBytes(device.download)}
                />
                <InfoLine
                  compact
                  title={t('supervision.connected_users.card.device.ssid')}
                  value={device.ssid || '-'}
                />
                <InfoLine
                  compact
                  title={t('supervision.connected_users.card.device.vlan')}
                  value={device.vlan || '-'}
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

const Card: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  
  const { loading, items } = useConnectedUsers();

  const user = useMemo(() => {
    return items.find(u => u.id === id);
  }, [items, id]);

  const handleClose = () => navigate('..');

  if (loading && !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Loading />
      </Box>
    );
  }

  if (!user) return null;

  if (isLargeScreen) {
    return (
      <SideContent>
        <CardContent user={user} onClose={handleClose} />
      </SideContent>
    );
  }

  return (
    <SlideInModal
      onClose={handleClose}
      hideHeader
      noPadding
      width="480px"
    >
      <Box sx={{ py: 2, px: 4 }}>
        <CardContent user={user} onClose={handleClose} />
      </Box>
    </SlideInModal>
  );
};

export default Card;